import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CardChecklistItemModel,
  CreateCardChecklistItemRequestModel,
  ReorderCardChecklistItemsRequestModel,
  ToggleCardChecklistItemRequestModel,
  UpdateCardChecklistItemRequestModel,
} from '../models/card-details-api.models';
import { asNumber, asRecord, asString } from './card-api.utils';

@Injectable({ providedIn: 'root' })
export class CardChecklistItemsApiService {
  private readonly http = inject(HttpClient);

  async getByChecklist(cardId: string, checklistId: string): Promise<CardChecklistItemModel[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/cards/${cardId}/checklists/${checklistId}/items`)
    );
    const arr = Array.isArray(response)
      ? response
      : Array.isArray((response as Record<string, unknown>)?.['items'])
        ? ((response as Record<string, unknown>)['items'] as unknown[])
        : [];
    return (arr as unknown[])
      .map((entry) => this.mapItem(asRecord(entry), checklistId))
      .filter((item) => !!item.id);
  }

  async create(
    cardId: string,
    checklistId: string,
    payload: CreateCardChecklistItemRequestModel
  ): Promise<CardChecklistItemModel | null> {
    const response = await firstValueFrom(
      this.http.post<unknown>(`/api/cards/${cardId}/checklists/${checklistId}/items`, payload)
    );
    const mapped = this.mapItem(this.extractItemEntity(response), checklistId);
    return mapped.id ? mapped : null;
  }

  async update(
    cardId: string,
    checklistId: string,
    itemId: string,
    payload: UpdateCardChecklistItemRequestModel
  ): Promise<CardChecklistItemModel | null> {
    const response = await firstValueFrom(
      this.http.put<unknown>(`/api/cards/${cardId}/checklists/${checklistId}/items/${itemId}`, payload)
    );
    const mapped = this.mapItem(this.extractItemEntity(response), checklistId);
    return mapped.id ? mapped : null;
  }

  async toggle(
    cardId: string,
    checklistId: string,
    itemId: string,
    payload: ToggleCardChecklistItemRequestModel
  ): Promise<CardChecklistItemModel | null> {
    const response = await firstValueFrom(
      this.http.patch<unknown>(
        `/api/cards/${cardId}/checklists/${checklistId}/items/${itemId}/toggle`,
        payload
      )
    );
    const mapped = this.mapItem(this.extractItemEntity(response), checklistId);
    return mapped.id ? mapped : null;
  }

  async delete(cardId: string, checklistId: string, itemId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`/api/cards/${cardId}/checklists/${checklistId}/items/${itemId}`)
    );
  }

  async reorder(
    cardId: string,
    checklistId: string,
    payload: ReorderCardChecklistItemsRequestModel
  ): Promise<void> {
    await firstValueFrom(
      this.http.put(`/api/cards/${cardId}/checklists/${checklistId}/items/reorder`, payload)
    );
  }

  private mapItem(raw: unknown, checklistId: string): CardChecklistItemModel {
    const entry = asRecord(raw);
    return {
      id: this.readString(entry, ['id', 'Id']),
      cardChecklistId:
        this.readString(entry, ['cardChecklistId', 'cardChecklistID', 'CardChecklistId']) || checklistId,
      title: this.readString(entry, ['title', 'Title']),
      isCompleted: this.readBoolean(entry, ['isCompleted', 'IsCompleted']),
      order: this.readNumber(entry, ['order', 'Order'], 0),
      completedAt: this.readNullableString(entry, ['completedAt', 'CompletedAt']),
      completedByUserId: this.readNullableString(entry, ['completedByUserId', 'CompletedByUserId']),
    };
  }

  private extractItemEntity(raw: unknown): unknown {
    if (Array.isArray(raw)) return raw[0] ?? {};
    const root = asRecord(raw);
    const data = asRecord(root['data']);

    const candidates: unknown[] = [
      root,
      root['item'],
      root['checklistItem'],
      root['result'],
      data,
      data['item'],
      data['checklistItem'],
      data['result'],
    ];

    for (const candidate of candidates) {
      const entry = asRecord(candidate);
      if (asString(entry['id'])) return entry;
    }

    return root;
  }

  private readString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = asString(source[key]);
      if (value) return value;
    }
    return '';
  }

  private readNullableString(source: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string') return value;
    }
    return null;
  }

  private readNumber(source: Record<string, unknown>, keys: string[], fallback: number): number {
    for (const key of keys) {
      const raw = source[key];
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'string' && raw.trim()) {
        const parsed = Number(raw);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return fallback;
  }

  private readBoolean(source: Record<string, unknown>, keys: string[]): boolean {
    for (const key of keys) {
      const raw = source[key];
      if (typeof raw === 'boolean') return raw;
    }
    return false;
  }
}
